'use client';
import { manipulateUrlSearchParams } from '@vs-shop/domain/string/urlSearchParams';
import { Option } from 'effect';
import { useRef } from 'react';

import BrandsGrid from './BrandsGrid';
import BrandsLoadNextPage from './BrandsLoadNextPage';
import BrandsPagination from './BrandsPagination';

import { fetchBrandsByPageAtom } from '../../../../effect/atom/brand/fetchBrandsByPage';
import {
  changeBrandPageAtomFn,
  currentAlphabetFilterAtom,
  currentPageAtom,
  currentSearchFilterAtom,
  loadMorePagesAtom,
} from '@vs-shop/engine/atom/brand/pagination';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomSet, useAtomValue } from '@effect/atom-react';

type Props = {
  canonicalUrl: string;
};

export default function BrandsForGivenFilters({ canonicalUrl }: Props) {
  const currentPage = useAtomValue(currentPageAtom);
  const alphabetFilter = useAtomValue(currentAlphabetFilterAtom);
  const searchFilter = useAtomValue(currentSearchFilterAtom);

  const brandsPerPageResponse = useAtomValue(
    fetchBrandsByPageAtom(currentPage, alphabetFilter, searchFilter)
  );

  const changePage = useAtomSet(changeBrandPageAtomFn, {
    mode: 'promise',
  });

  const loadMorePages = useAtomSet(loadMorePagesAtom, {
    mode: 'value',
  });

  const ref = useRef<HTMLDivElement>(null);

  function scrollToBrandsGrid(): void {
    if (!ref.current) return;

    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'center',
    });
  }

  // Build canonical URL with current filters
  const canonicalUrlWithFilters = manipulateUrlSearchParams(canonicalUrl, {
    alphabet: alphabetFilter,
    search: searchFilter,
    page: null,
  });

  return (
    <div ref={ref}>
      <BrandsGrid
        currentPage={currentPage}
        alphabetFilter={alphabetFilter}
        searchFilter={searchFilter}
      />

      {AsyncResult.builder(brandsPerPageResponse)
        .onSuccess((response) =>
          Option.match(Option.fromNullishOr(response['hydra:view']), {
            onNone: () => null,
            onSome: (hydraViewExtended) => (
              <BrandsPagination
                hydraViewExtended={hydraViewExtended}
                changePage={changePage}
                scrollToBrandsGrid={scrollToBrandsGrid}
                canonicalUrl={canonicalUrlWithFilters}
              />
            ),
          })
        )
        .render()}

      {AsyncResult.builder(brandsPerPageResponse)
        .onSuccess((response) =>
          Option.match(Option.fromNullishOr(response['hydra:view']), {
            onNone: () => null,
            onSome: (hydraViewExtended) => (
              <BrandsLoadNextPage
                hydraViewExtended={hydraViewExtended}
                loadMorePages={loadMorePages}
              />
            ),
          })
        )
        .render()}
    </div>
  );
}
